import type { Amount, FiatPrice } from "@b2bportal/air-booking-api";
import type { Prices } from "@b2bportal/core-types";
import { DiscountEnum, type WalletOffer } from "@b2bportal/wallet-offer-api";

export const amountToFiatPrice = (
  amount?: Amount
): Omit<FiatPrice, "currencySymbol"> => ({
  value: amount?.amount || 0,
  currencyCode: amount?.currency || "CAD",
});

export const noFractionOptions = {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
};

const fractionalDigits = {
  CAD: 100,
};

export const toSaneDecimalAmount = (
  a: number,
  b: number,
  currencyCode: string
): number => {
  const tens = fractionalDigits[currencyCode];
  if (tens == null) {
    throw new Error(`${currencyCode} is not supported`);
  }
  if (tens === 1) {
    return a - b;
  }
  return Math.round((a - b) * tens) / tens;
};

export const getOfferValue = (
  offer?: WalletOffer,
  baseAmount?: FiatPrice
): FiatPrice | undefined => {
  if (offer == null) {
    return undefined;
  }

  if (offer.discount.Discount === DiscountEnum.FiatAmount) {
    return offer.discount.amount.fiat;
  }

  if (baseAmount == null) {
    return undefined;
  }

  let amount = { ...baseAmount };
  const discounted = baseAmount.value * offer.discount.percentage;
  if (offer.discount.maxDiscount?.fiat != null) {
    amount.value = Math.round(
      Math.min(discounted, offer.discount.maxDiscount.fiat.value)
    );
  } else {
    amount.value = discounted;
  }

  return amount;
};

/**
 * Adjusts incremental package prices for display to avoid -$0 formatting.
 *
 * Rounds negative fiat values away from zero using Math.floor() so small negative
 * values like -0.67 display as "-$1" instead of "-$0".
 *
 * @param prices - The incremental price change from package state
 * @returns Modified prices with floored negative fiat values
 *
 * @example
 * adjustIncrementalPriceForDisplay({ fiat: { value: -0.67, currencyCode: "CAD" } })
 * // Returns: { fiat: { value: -1, currencyCode: "CAD" } } → displays as "-$1"
 */
export const adjustIncrementalPriceForDisplay = (prices: Prices): Prices => {
  // Early return if not negative or already an integer
  if (prices.fiat.value >= 0 || Number.isInteger(prices.fiat.value)) {
    return prices;
  }

  return {
    ...prices,
    fiat: {
      ...prices.fiat,
      value: Math.floor(prices.fiat.value),
    },
  };
};
